import { Component, computed, effect, inject, Injector, input, runInInjectionContext, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import {
  BehaviorSubject,
  filter,
  interval,
  merge,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { CountdownManager } from './countdown-manager.class';

@Component({
  imports: [MatIcon],
  selector: 'scw-countdown',
  styleUrl: './countdown.component.scss',
  templateUrl: './countdown.component.html',
})
export class CountdownComponent {
  public manager = input.required<CountdownManager>();
  private readonly injector = inject(Injector);

  public readonly countdown = signal(-1);
  private readonly noManagerFailsafe$ = new Observable<number>().pipe(startWith(-1));
  private readonly initialCountdown$ = toObservable(
    computed(() => this.manager()?.initialCountdown ?? this.noManagerFailsafe$),
  );
  private readonly setCountdownAdapter = new BehaviorSubject<Observable<number>>(this.noManagerFailsafe$);
  private countdownSubscription: Subscription | undefined;

  constructor() {
    effect(() => {
      const setCountdownSubject = this.manager()?.setCountdownSubject ?? this.noManagerFailsafe$;

      this.setCountdownAdapter.next(setCountdownSubject);
    });

    this.setCountdownAdapter
      .pipe(
        switchMap((setCountdownSubject) => merge(this.initialCountdown$, setCountdownSubject)),
        takeUntilDestroyed(),
      )
      .subscribe((countdown) => {
        this.countdown.set(countdown);

        if (this.manager()?.remainingSecondsSubject) {
          this.manager().remainingSecondsSubject.next(countdown);
        }

        const pageRefreshInterval: Observable<number> = interval(1000);
        this.countdownSubscription?.unsubscribe();

        if (countdown === -1) {
          return;
        }

        const pauseStatus$ = this.manager()?.pauseSubject ?? of(false);

        runInInjectionContext(this.injector, () => {
          this.countdownSubscription = pageRefreshInterval
            .pipe(
              takeUntilDestroyed(),
              withLatestFrom(pauseStatus$),
              filter(([_, isPaused]) => !isPaused),
            )
            .subscribe(() => {
              this.countdown.update((val) => {
                const newVal = val - 1;

                if (this.manager()?.remainingSecondsSubject) {
                  this.manager().remainingSecondsSubject.next(newVal);
                }

                return newVal;
              });

              if (this.countdown() === 0) {
                this.manager().timeoutSubject.next(false);
              }
            });
        });
      });
  }
}
